/**
 * 170 — goods going back to a supplier: the rules the return dialog shows
 * before the server books it.
 *
 * THE SERVER DECIDES (return_to_supplier): it groups the lines by product and
 * purchase line, computes every total, checks the stock and the purchase
 * line's room, and moves the cost.
 * These functions exist so the dialog shows what the server will accept and
 * book — the same rule, never a second one (the cost rule is migration 170's,
 * to the tetri).
 */
import type { Key } from './i18n'
import { mulTetri } from './money-round'
import { roundWeight } from './weight'
import type { CashMovement, Expense, Product, ProductUnit, Purchase, PurchaseItem, Supplier, SupplierReturn, SupplierReturnItem } from './mock-data'

export type ReturnLine = {
  product_id: string
  /** The purchase line it came from, when the return was opened from a purchase. */
  purchase_item_id: string | null
  quantity: number
  /** What the supplier credits per unit. */
  unit_credit: number
}

const tetri = (n: number) => Math.round(n * 100)

/** A line's money in tetri: round(q × credit, 2) on the exact decimals, as the server computes it. */
const lineCredit = (l: Pick<ReturnLine, 'quantity' | 'unit_credit'>) => mulTetri(l.quantity, l.unit_credit)

/** A line's money in ₾, as the dialog shows it under the line. */
export const returnLineMoney = (l: Pick<ReturnLine, 'quantity' | 'unit_credit'>): number => lineCredit(l) / 100

/** The return's total: every line rounded to the tetri first, then added. */
export function returnTotal(lines: ReturnLine[]): number {
  return lines.reduce((s, l) => s + lineCredit(l), 0) / 100
}

/**
 * The cost rule (migration 170): the goods leave the books at what the
 * supplier credits, and the WAC of what remains absorbs the difference. Where
 * it cannot — the product empties, or the credit is above the value carried —
 * the signed difference is kept: positive = a loss (booked as a write-off),
 * negative = a gain (shown, not booked).
 */
export function returnCost(c: { orgQty: number; wac: number; qty: number; credit: number }): { newWac: number; costDiff: number } {
  const value = c.orgQty * c.wac
  const left = c.orgQty - c.qty
  if (left > 0) {
    if (value - c.credit >= 0) return { newWac: tetri((value - c.credit) / left) / 100, costDiff: 0 }
    return { newWac: 0, costDiff: tetri(value - c.credit) / 100 }
  }
  return { newWac: c.wac, costDiff: tetri(c.qty * c.wac - c.credit) / 100 }
}

/**
 * The owner's M16 („კი დაამატე", 2026-10-18): a credit far below the cost — under
 * half of it — while stock stays puts the whole loss into the cost of what stays
 * (the cost rule above): 10 @ ₾2 with 8 back at ₾0 leaves 2 that cost ₾10 each,
 * and the margins read wrong until they sell. The dialog says so, with the cost
 * the rest will carry. Nothing stays, or no cost known: nothing to warn (an
 * emptied product books its loss as an expense instead).
 */
export function lowCreditWarning(
  l: Pick<ReturnLine, 'quantity' | 'unit_credit'>,
  p: { orgQty: number; wac: number },
): { wac: number; next: number; left: number } | null {
  const left = Math.round((p.orgQty - l.quantity) * 1000) / 1000
  if (!(p.wac > 0) || !(left > 0) || !(l.unit_credit < p.wac / 2)) return null
  const { newWac } = returnCost({ orgQty: p.orgQty, wac: p.wac, qty: l.quantity, credit: lineCredit(l) / 100 })
  if (!(newWac > p.wac)) return null   // a big stock: the rest costs the same to the tetri
  return { wac: p.wac, next: newWac, left }
}

/** The unit credit to offer: this supplier's latest purchase price of the product, else the WAC. */
export function defaultUnitCredit(productId: string, supplierId: string, purchases: Purchase[], items: PurchaseItem[], wac: number): number {
  const fromHim = new Map(purchases.filter((p) => p.supplier_id === supplierId).map((p) => [p.id, p.created_at]))
  let best: { at: string; cost: number } | null = null
  for (const i of items) {
    if (i.product_id !== productId) continue
    const at = fromHim.get(i.purchase_id)
    if (at === undefined) continue
    if (!best || at > best.at) best = { at, cost: i.unit_cost }
  }
  return best ? best.cost : wac
}

/** What is left of a purchase line to return: its quantity minus earlier returns from it. */
export function returnRoom(item: Pick<PurchaseItem, 'id' | 'quantity'>, earlier: Pick<SupplierReturnItem, 'purchase_item_id' | 'quantity'>[]): number {
  const taken = earlier.reduce((s, r) => (r.purchase_item_id === item.id ? s + r.quantity : s), 0)
  return Math.max(0, Math.round((item.quantity - taken) * 1000) / 1000)
}

/** The first rule a line breaks, in words the dialog shows under it. */
export function returnLineError(
  l: ReturnLine,
  ctx: { unit: ProductUnit; branchStock: number; room: number | null; service: boolean },
): Key | null {
  if (ctx.service) return 'store.refusal.serviceNoStock'
  if (!(l.quantity > 0)) return 'sup.ret.errQty'
  if (ctx.unit === 'piece' && !Number.isInteger(l.quantity)) return 'sup.ret.errWhole'
  if (roundWeight(l.quantity) !== l.quantity) return 'sup.ret.errDecimals'    // stock keeps 3 decimals
  if (!(l.unit_credit >= 0)) return 'sup.ret.errCredit'
  if (l.quantity > ctx.branchStock + 0.0005) return 'sup.ret.errStock'
  if (ctx.room !== null && l.quantity > ctx.room + 0.0005) return 'sup.ret.errRoom'
  return null
}

/**
 * The arguments of return_to_supplier: the dialog's ref, each line as typed (the
 * server groups them by product and purchase line and sums each line's rounded
 * money — the total the dialog showed, to the tetri), the shift only for cash.
 */
export function returnRpcArgs(r: {
  supplierId: string; settle: 'debt' | 'cash'; purchaseId: string | null; note: string | null
  lines: ReturnLine[]; shiftId: string | null; branchId: string | null; clientRef: string
}) {
  return {
    p_supplier_id: r.supplierId,
    p_items: r.lines.map((l) => ({
      product_id: l.product_id, purchase_item_id: l.purchase_item_id, quantity: l.quantity, unit_credit: l.unit_credit,
    })),
    p_settle: r.settle,
    p_shift_id: r.settle === 'cash' ? r.shiftId : null,
    p_branch_id: r.branchId,
    p_purchase_id: r.purchaseId,
    p_note: r.note,
    p_client_ref: r.clientRef,
  }
}

/** What return_to_supplier answers (private.supplier_return_json, 170). */
export type ReturnAnswer = {
  return: SupplierReturn
  items: SupplierReturnItem[]
  supplier: Supplier | null
  movement: CashMovement | null
  expenses: Expense[]
  products: { id: string; purchase_price: number; org_quantity: number; branch_quantity: number | null }[]
}

type ReturnSlices = {
  products: Product[]; suppliers: Supplier[]; cashMovements: CashMovement[]
  supplierReturns: SupplierReturn[]; supplierReturnItems: SupplierReturnItem[]; expenses: Expense[]
}

const upsert = <T extends { id: string }>(list: T[], rows: T[]): T[] => {
  if (rows.length === 0) return list
  const ids = new Set(rows.map((r) => r.id))
  return [...rows, ...list.filter((x) => !ids.has(x.id))]
}

/**
 * The cache takes the server's answer as it is: the balance, each product's
 * cost and stock (a multi-branch copy keeps the branch's stock in `quantity`
 * and the org's in `org_quantity`), the movement and the write-off — by id, so
 * a replayed answer never adds anything twice.
 */
export function applyReturnAnswer<S extends ReturnSlices>(state: S, a: ReturnAnswer): ReturnSlices {
  const byId = new Map(a.products.map((p) => [p.id, p]))
  return {
    products: state.products.map((p) => {
      const n = byId.get(p.id)
      if (!n) return p
      return p.org_quantity !== undefined
        ? { ...p, purchase_price: n.purchase_price, quantity: n.branch_quantity ?? p.quantity, org_quantity: n.org_quantity }
        : { ...p, purchase_price: n.purchase_price, quantity: n.org_quantity }
    }),
    suppliers: a.supplier ? state.suppliers.map((x) => (x.id === a.supplier!.id ? { ...x, ...a.supplier } : x)) : state.suppliers,
    cashMovements: a.movement ? upsert(state.cashMovements, [a.movement]) : state.cashMovements,
    supplierReturns: upsert(state.supplierReturns, [a.return]),
    supplierReturnItems: upsert(state.supplierReturnItems, a.items),
    expenses: upsert(state.expenses, a.expenses),
  }
}
